<script setup>
import { useGamesStore } from '@/stores/games';
import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const games = useGamesStore()
const { getGame, isUrl } = storeToRefs(games)
const route = useRoute()
const header = ref(true)
const height = ref(0)
const o = ref('')
const iframeRef = ref(null)
const isFullscreen = ref(false)

const isMobileSafari = computed(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check if the device is mobile
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(userAgent);

    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isSamsungBrowser = /SamsungBrowser/i.test(userAgent);

    return isMobile && (isSafari || isSamsungBrowser);
})

const setHeight = () => {
    const headerHeight = header ? document.getElementById("header")?.offsetHeight : 0
    height.value = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - headerHeight
}

const hideHeader = () => {
    let header = document.getElementById('header')
    header.style.display = 'none'
    header.value = false

    let gameFrame = document.getElementById('lsframe')
    gameFrame.style.height = document.documentElement.clientHeight || window.innerHeight + 'px'
}

const showHeader = () => {
    let header = document.getElementById('header')
    header.style.display = 'block'
    header.value = true

    let gameFrame = document.getElementById('lsframe')
    gameFrame.style.height = '100%'
}

const resizeHandler = () => {
    const orientation = window.screen.orientation?.type;

    if (orientation === 'landscape-primary' || orientation === 'landscape-secondary') {
        hideHeader();
    } else if (orientation === 'portrait-primary' || orientation === 'portrait-secondary') {
        showHeader();
    }

    setHeight();
};

const registerResizeEvent = () => {
    // o.value = isMobileSafari.value
    if (isMobileSafari.value) {
        window.addEventListener("resize", resizeHandler);
    } else {
        screen.orientation.addEventListener("change", resizeHandler);
    }
}

const removeResizeEvent = () => {
    if (isMobileSafari.value) {
        window.removeEventListener("resize", resizeHandler);
    } else {
        screen.orientation.removeEventListener("change", resizeHandler);
    }
}

const toggleFullscreen = async () => {
    if (!iframeRef.value) return

    if (isFullscreen.value) {
        // Exit fullscreen
        exitManualFullscreen()
        return
    }

    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(navigator.userAgent)

    if (isMobile) {
        // On mobile, use CSS-based fullscreen since native API is unreliable
        enterManualFullscreen()
    } else {
        // On desktop, try native fullscreen first, fallback to manual
        try {
            const iframe = iframeRef.value
            if (iframe.requestFullscreen) {
                await iframe.requestFullscreen()
            } else if (iframe.webkitRequestFullscreen) {
                await iframe.webkitRequestFullscreen()
            } else {
                enterManualFullscreen()
            }
        } catch (error) {
            enterManualFullscreen()
        }
    }
}

const enterManualFullscreen = () => {
    if (!iframeRef.value) return

    const iframe = iframeRef.value

    // Industry standard approach: Apply styles to container AND iframe
    const container = iframe.parentElement

    // Set container as fullscreen overlay
    container.style.position = 'fixed'
    container.style.top = '0'
    container.style.left = '0'
    container.style.right = '0'
    container.style.bottom = '0'
    container.style.width = '100vw'
    container.style.height = '100vh'
    container.style.zIndex = '999998'
    container.style.background = '#000'
    container.style.margin = '0'
    container.style.padding = '0'

    // Make iframe fill the container completely
    iframe.style.position = 'absolute'
    iframe.style.top = '0'
    iframe.style.left = '0'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = 'none'
    iframe.style.margin = '0'
    iframe.style.padding = '0'
    iframe.style.display = 'block'

    // Hide everything else and prevent scroll
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    // Add fullscreen class to body for additional CSS control
    document.body.classList.add('iframe-fullscreen')

    // Mobile browser UI optimization
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        // Hide address bar on mobile
        setTimeout(() => {
            window.scrollTo(0, 1)
            // Force height recalculation
            container.style.height = window.innerHeight + 'px'
            iframe.style.height = '100%'
        }, 100)

        // Handle orientation changes
        window.addEventListener('orientationchange', handleOrientationChange)
    }

    // Update state
    isFullscreen.value = true
    games.setGameFullscreen(true)
}

const handleOrientationChange = () => {
    if (isFullscreen.value && iframeRef.value) {
        setTimeout(() => {
            const container = iframeRef.value.parentElement
            container.style.height = window.innerHeight + 'px'
        }, 200)
    }
}

const exitManualFullscreen = () => {
    if (!iframeRef.value) return

    const iframe = iframeRef.value
    const container = iframe.parentElement

    // Reset container styles
    container.style.position = ''
    container.style.top = ''
    container.style.left = ''
    container.style.right = ''
    container.style.bottom = ''
    container.style.width = ''
    container.style.height = ''
    container.style.zIndex = ''
    container.style.background = ''
    container.style.margin = ''
    container.style.padding = ''

    // Reset iframe styles
    iframe.style.position = ''
    iframe.style.top = ''
    iframe.style.left = ''
    iframe.style.right = ''
    iframe.style.bottom = ''
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = ''
    iframe.style.margin = ''
    iframe.style.padding = ''
    iframe.style.display = ''

    // Restore body scroll and remove fullscreen class
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    document.body.classList.remove('iframe-fullscreen')

    // Remove orientation listener
    window.removeEventListener('orientationchange', handleOrientationChange)

    // Update state
    isFullscreen.value = false
    games.setGameFullscreen(false)
}

// Force cleanup function for route changes and navigation
const forceCleanupFullscreen = () => {
    // Force exit fullscreen regardless of state
    if (document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
    } else if (document.webkitExitFullscreen && document.webkitFullscreenElement) {
        document.webkitExitFullscreen()
    }

    // Force reset all styles even if iframe doesn't exist
    const allIframes = document.querySelectorAll('iframe')
    allIframes.forEach(iframe => {
        const container = iframe.parentElement
        if (container) {
            // Reset container
            container.style.cssText = ''
        }
        // Reset iframe
        iframe.style.cssText = ''
    })

    // Force restore body
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    document.body.classList.remove('iframe-fullscreen')

    // Remove all listeners
    window.removeEventListener('orientationchange', handleOrientationChange)
    document.removeEventListener('fullscreenchange', handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', handleFullscreenChange)

    // CRITICAL: Force reset component state immediately
    isFullscreen.value = false

    // Force reset store state
    games.setGameFullscreen(false)
}

const handleFullscreenChange = () => {
    const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    )

    isFullscreen.value = isCurrentlyFullscreen
    games.setGameFullscreen(isCurrentlyFullscreen)

    if (!isCurrentlyFullscreen) {
        // Clean up listeners when exiting fullscreen
        document.removeEventListener('fullscreenchange', handleFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
    }
}

onMounted(() => {
    // if (isUrl.value) {
    //     window.open(getGame.value, '_blank')
    // }

    // setHeight()
    resizeHandler()
    registerResizeEvent()
})

// Watch for route changes and cleanup fullscreen
watch(() => route.path, (newPath, oldPath) => {
    if (isFullscreen.value) {
        // Immediately reset state first, then cleanup
        isFullscreen.value = false
        games.setGameFullscreen(false)
        forceCleanupFullscreen()
    }
}, { immediate: false })

// Listen for browser back/forward navigation
window.addEventListener('popstate', () => {
    if (isFullscreen.value) {
        // Immediately reset state first, then cleanup
        isFullscreen.value = false
        games.setGameFullscreen(false)
        forceCleanupFullscreen()
    }
})

onBeforeUnmount(() => {
    removeResizeEvent()
    showHeader()

    // Force complete cleanup on unmount
    forceCleanupFullscreen()
})

// Also cleanup when component is destroyed
onUnmounted(() => {
    forceCleanupFullscreen()
})
</script>

<template>
    {{ o }}
    <div :style="{height: height + 'px'}" v-if ="!isUrl" v-html="getGame"></div>
    <div :style="{height: height + 'px'}" v-else class="relative">
        <iframe
            ref="iframeRef"
            id="lsframe"
            :src="getGame"
            frameborder="0"
            allowfullscreen
            webkitallowfullscreen
            mozallowfullscreen
            width="100%"
            height="100%"
            allow="geolocation;camera;microphone;fullscreen"
            style="border: none; background: #000;"
        >
        </iframe>

        <!-- Fullscreen entry button (centered overlay when not fullscreen) -->
        <div
            v-if="!isFullscreen"
            @click="toggleFullscreen"
            class="absolute inset-0 bg-black bg-opacity-20 cursor-pointer flex items-center justify-center z-10"
        >
            <div class="bg-black bg-opacity-70 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition-all">
                ⛶ Tap for Fullscreen
            </div>
        </div>

    <!-- Fullscreen exit button (absolute positioned within component when fullscreen) -->
    <button
        v-if="isFullscreen && isUrl"
        @click="exitManualFullscreen"
        class="absolute top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded text-white text-sm font-bold shadow-lg hover:bg-opacity-90 transition-all"
        style="z-index: 999999; background-color: rgba(0, 0, 0, 0.7); position: fixed;"
    >
        ✕
    </button>
    </div>
</template>

<style scoped>
/* Ensure no gaps or spacing issues in fullscreen */
.relative {
    position: relative;
    overflow: hidden;
}

/* Global fullscreen styles */
:global(body.iframe-fullscreen) {
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    position: fixed !important;
    width: 100% !important;
    height: 100% !important;
}

:global(html.iframe-fullscreen),
:global(body.iframe-fullscreen) {
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    outline: 0 !important;
}

/* Remove any potential spacing from the viewport */
:global(body.iframe-fullscreen *) {
    box-sizing: border-box;
}
</style>
